import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';

const TripContext = createContext(null);

export const useTrip = () => {
  const context = useContext(TripContext);
  if (!context) {
    throw new Error('useTrip must be used within a TripProvider');
  }
  return context;
};

export const TripProvider = ({ children }) => {
  const { api } = useAuth();
  const [trips, setTrips] = useState([]);
  const [currentTrip, setCurrentTrip] = useState(null);
  const [members, setMembers] = useState([]);
  const [itinerary, setItinerary] = useState([]);
  const [payments, setPayments] = useState([]);
  const [votes, setVotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/trips');
      setTrips(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching trips:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchTrip = useCallback(async (tripId) => {
    setLoading(true);
    try {
      const response = await api.get(`/trips/${tripId}`);
      setCurrentTrip(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching trip:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [api]);

  const createTrip = async (tripData) => {
    const response = await api.post('/trips', tripData);
    setTrips([...trips, response.data]);
    return response.data;
  };

  const updateTrip = async (tripId, tripData) => {
    const response = await api.put(`/trips/${tripId}`, tripData);
    setTrips(trips.map(t => t.id === tripId ? response.data : t));
    if (currentTrip?.id === tripId) {
      setCurrentTrip(response.data);
    }
    return response.data;
  };

  const deleteTrip = async (tripId) => {
    await api.delete(`/trips/${tripId}`);
    setTrips(trips.filter(t => t.id !== tripId));
    if (currentTrip?.id === tripId) {
      setCurrentTrip(null);
    }
  };

  const joinTrip = async (inviteCode) => {
    const response = await api.post(`/trips/join-by-code?invite_code=${inviteCode}`);
    // Response now contains status: 'pending' or 'approved'
    return response.data;
  };

  const leaveTrip = async (tripId) => {
    await api.post(`/trips/${tripId}/leave`);
    setTrips(trips.filter(t => t.id !== tripId));
    if (currentTrip?.id === tripId) {
      setCurrentTrip(null);
    }
  };

  // Join request management (organizer only)
  const fetchPendingRequests = useCallback(async (tripId) => {
    const response = await api.get(`/trips/${tripId}/pending-requests`);
    return response.data;
  }, [api]);

  const approveMember = async (tripId, memberId) => {
    const response = await api.post(`/trips/${tripId}/members/${memberId}/approve`);
    await fetchMembers(tripId);
    await fetchTrip(tripId);
    return response.data;
  };

  const rejectMember = async (tripId, memberId) => {
    const response = await api.post(`/trips/${tripId}/members/${memberId}/reject`);
    await fetchMembers(tripId);
    await fetchTrip(tripId);
    return response.data;
  };

  const closeRegistrations = async (tripId) => {
    const response = await api.post(`/trips/${tripId}/close-registrations`);
    await fetchTrip(tripId);
    return response.data;
  };

  const openRegistrations = async (tripId) => {
    const response = await api.post(`/trips/${tripId}/open-registrations`);
    await fetchTrip(tripId);
    return response.data;
  };

  const fetchMembers = useCallback(async (tripId) => {
    try {
      const response = await api.get(`/trips/${tripId}/members`);
      setMembers(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching members:', error);
      throw error;
    }
  }, [api]);

  const fetchItinerary = useCallback(async (tripId) => {
    try {
      const response = await api.get(`/trips/${tripId}/itinerary`);
      // API returns List[DayItinerary], flatten to List[ItineraryItem]
      const days = response.data || [];
      const flatItems = days.flatMap(day => day.items || []);
      setItinerary(flatItems);
      return flatItems;
    } catch (error) {
      console.error('Error fetching itinerary:', error);
      throw error;
    }
  }, [api]);

  const createItineraryItem = async (tripId, itemData) => {
    const response = await api.post(`/trips/${tripId}/itinerary`, {
      ...itemData,
      trip_id: tripId,
    });
    await fetchItinerary(tripId);
    return response.data;
  };

  const updateItineraryItem = async (tripId, itemId, itemData) => {
    const response = await api.put(`/trips/${tripId}/itinerary/${itemId}`, itemData);
    await fetchItinerary(tripId);
    return response.data;
  };

  const deleteItineraryItem = async (tripId, itemId) => {
    await api.delete(`/trips/${tripId}/itinerary/${itemId}`);
    await fetchItinerary(tripId);
  };

  const fetchPayments = useCallback(async (tripId) => {
    try {
      const response = await api.get(`/trips/${tripId}/payments`);
      setPayments(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching payments:', error);
      throw error;
    }
  }, [api]);

  const createPayment = async (tripId, paymentData) => {
    const response = await api.post(`/trips/${tripId}/payments`, {
      ...paymentData,
      trip_id: tripId,
    });
    await fetchPayments(tripId);
    return response.data;
  };

  const markAsPaid = async (tripId, paymentId, amount, proofUrl = null) => {
    const params = new URLSearchParams({ amount: amount.toString() });
    if (proofUrl) params.append('proof_url', proofUrl);
    const response = await api.post(`/trips/${tripId}/payments/${paymentId}/pay?${params}`);
    await fetchPayments(tripId);
    return response.data;
  };

  const fetchVotes = useCallback(async (tripId) => {
    try {
      const response = await api.get(`/trips/${tripId}/votes`);
      setVotes(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching votes:', error);
      throw error;
    }
  }, [api]);

  const createVote = async (tripId, voteData) => {
    const response = await api.post(`/trips/${tripId}/votes`, {
      ...voteData,
      trip_id: tripId,
    });
    await fetchVotes(tripId);
    return response.data;
  };

  const castVote = async (tripId, voteId, selectedOptions) => {
    const response = await api.post(`/trips/${tripId}/votes/${voteId}/cast`, {
      selected_options: selectedOptions,
    });
    await fetchVotes(tripId);
    return response.data;
  };

  // Destination voting functions
  const voteForDestination = async (tripId, optionId) => {
    const response = await api.post(`/trips/${tripId}/vote-destination/${optionId}`);
    await fetchTrip(tripId);
    return response.data;
  };

  const removeVoteForDestination = async (tripId, optionId) => {
    const response = await api.delete(`/trips/${tripId}/vote-destination/${optionId}`);
    await fetchTrip(tripId);
    return response.data;
  };

  const selectWinningDestination = async (tripId, optionId) => {
    const response = await api.post(`/trips/${tripId}/select-destination/${optionId}`);
    await fetchTrip(tripId);
    return response.data;
  };

  const addDestinationOption = async (tripId, name, image = null, placeData = null) => {
    const response = await api.post(`/trips/${tripId}/voting/add-option`, { name, image, place_data: placeData });
    await fetchTrip(tripId);
    return response.data;
  };

  const removeDestinationOption = async (tripId, optionId) => {
    const response = await api.delete(`/trips/${tripId}/voting/remove-option/${optionId}`);
    await fetchTrip(tripId);
    return response.data;
  };

  const updateDestinationOption = async (tripId, optionId, updateData) => {
    const response = await api.put(`/trips/${tripId}/voting/update-option/${optionId}`, updateData);
    await fetchTrip(tripId);
    return response.data;
  };

  const uploadCoverImage = async (tripId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/trips/${tripId}/cover-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    await fetchTrip(tripId);
    return response.data;
  };

  const removeCoverImage = async (tripId) => {
    const response = await api.delete(`/trips/${tripId}/cover-image`);
    await fetchTrip(tripId);
    return response.data;
  };

  const value = {
    trips,
    currentTrip,
    members,
    itinerary,
    payments,
    votes,
    loading,
    fetchTrips,
    fetchTrip,
    createTrip,
    updateTrip,
    deleteTrip,
    joinTrip,
    leaveTrip,
    fetchPendingRequests,
    approveMember,
    rejectMember,
    closeRegistrations,
    openRegistrations,
    fetchMembers,
    fetchItinerary,
    createItineraryItem,
    updateItineraryItem,
    deleteItineraryItem,
    fetchPayments,
    createPayment,
    markAsPaid,
    fetchVotes,
    createVote,
    castVote,
    voteForDestination,
    removeVoteForDestination,
    selectWinningDestination,
    addDestinationOption,
    removeDestinationOption,
    updateDestinationOption,
    uploadCoverImage,
    removeCoverImage,
    setCurrentTrip,
  };

  return <TripContext.Provider value={value}>{children}</TripContext.Provider>;
};

export default TripContext;
